import { useEffect, useState } from "react";
import { apiRequest } from "../config/api";

export interface TournamentAggregate {
  tournamentsPlayed: number;
  totalMatches: number;
  wins: number;
  losses: number;
  winRate: number;
  gamesWon: number;
  gamesLost: number;
  gameDiff: number;
  groupStage: { matches: number; wins: number; losses: number; winRate: number };
  knockout: { matches: number; wins: number; losses: number; winRate: number };
}

export interface TournamentPartnerStat {
  partnerId: string;
  partnerName: string;
  tournamentsPlayed: number;
  matchesPlayed: number;
  matchesWon: number;
  winRate: number;
}

export interface TournamentOpponentStat {
  opponentId: string;
  opponentName: string;
  matchesPlayed: number;
  wins: number;
  losses: number;
  winRate: number;
}

export interface TournamentPointsTrendEntry {
  tournamentId: string;
  tournamentName: string;
  startDate: string | Date;
  points: number;
}

export interface TournamentStats {
  playerId: string;
  aggregate: TournamentAggregate;
  partners: TournamentPartnerStat[];
  bestPartner: TournamentPartnerStat | null;
  opponents: TournamentOpponentStat[];
  pointsTrend: TournamentPointsTrendEntry[];
}

export function useTournamentStats(playerId: string) {
  const [data, setData] = useState<TournamentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiRequest(`/api/players/${playerId}/tournament-stats`);
        if (!response.ok) {
          throw new Error("Failed to load tournament stats");
        }
        const json = await response.json();
        if (!cancelled) {
          setData(json);
        }
      } catch (err) {
        console.error("Error fetching tournament stats:", err);
        if (!cancelled) {
          setError("An error occurred while loading tournament stats");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchStats();

    return () => {
      cancelled = true;
    };
  }, [playerId]);

  return { data, loading, error };
}
